import SaveIcon from '@mui/icons-material/Save';
import Form from '@rjsf/core';
import { CustomValidator, ErrorSchema } from '@rjsf/utils';
import { UiSchema } from '@rjsf/utils/src/types.ts';
import { FileManagement, Prompt, Secret, WorkflowNode } from 'api/model';
import { useKeyboard } from 'hooks/useKeyboard';
import { JSONSchema7 } from 'json-schema';
import { ReactNode, useCallback, useState } from 'react';
import { Node } from 'reactflow';
import CustomForm from './CustomForm';
import FormButtons from './FormButtons';

export type FormBlockNode = Node<WorkflowNode> & { columns: string[] };

export type FormBlockContext = {
  nodeId?: string;
  inputs?: FormBlockNode[];
  outputs?: FormBlockNode[];
  extra?: Record<string, string>;
  formData?: Record<string, any>;
  isTooltipView?: boolean;
  prompts?: Prompt[];
  secrets?: Secret[];
  files?: FileManagement[];
  pythonFunctionInputCount?: number;
};

type OnCancelCallback = {
  (): void;
  (isDirty: boolean): void;
};

export type FormBlockProps<T> = {
  schema: JSONSchema7;
  uiSchema?: UiSchema;
  onSubmit?: (formData: T) => void;
  onChange?: (formData: T) => void;
  onCancel?: OnCancelCallback;
  initialValues?: T;
  children?: ReactNode;
  cancelBtnTxt?: string;
  submitBtnTxt?: string;
  submitBtnIcon?: ReactNode;
  cancelBtnIcon?: ReactNode;
  isLoading?: boolean;
  formId?: string;
  disabled?: boolean;
  showButtons?: boolean;
  formContext?: FormBlockContext;
  ref?: React.Ref<Form<T>>;
  validator?: CustomValidator<T>;
  extraErrors?: ErrorSchema;
  isTooltipView?: boolean;
};

const FormBlock = <T extends object>({
  onSubmit,
  onChange,
  onCancel,
  schema,
  uiSchema,
  initialValues = {} as T,
  cancelBtnTxt = 'Cancel',
  submitBtnTxt = 'Submit',
  submitBtnIcon = <SaveIcon />,
  cancelBtnIcon,
  isLoading = false,
  children,
  formId = 'form-block',
  disabled = false,
  showButtons = true,
  formContext,
  ref,
  validator,
  extraErrors,
}: FormBlockProps<T>) => {
  const [formData, setFormData] = useState<T>(initialValues);

  const handleSubmit = useCallback(() => {
    if (isLoading) {
      return;
    }
    if (onSubmit) {
      onSubmit(formData);
    }
  }, [formData, onSubmit, isLoading]);

  const handleOnCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);

  const handleOnChange = (formData: T) => {
    setFormData(formData);
    if (onChange) {
      onChange(formData);
    }
  };

  useKeyboard({
    keyDown: (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleOnCancel();
      }
    },
  });

  return (
    <div>
      <CustomForm
        schema={schema}
        uiSchema={uiSchema ?? {}}
        onSubmit={handleSubmit}
        onChange={handleOnChange}
        initialValues={initialValues}
        children={children}
        isLoading={isLoading}
        formId={formId}
        disabled={disabled}
        formContext={formContext}
        validator={validator}
        extraErrors={extraErrors}
        ref={ref}
      />
      {showButtons && (
        <div className="flex justify-end gap-2 mt-5">
          <FormButtons
            onCancel={handleOnCancel}
            cancelBtnTxt={cancelBtnTxt}
            submitBtnTxt={submitBtnTxt}
            submitBtnIcon={submitBtnIcon}
            cancelBtnIcon={cancelBtnIcon ?? null}
            isLoading={isLoading}
            formId={formId}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};

export default FormBlock;
